import { reactive } from 'vue'

/**
 * Promise-based imperative confirmation dialog.
 *
 * Replaces native `window.confirm()` with a themed, accessible dialog that
 * distinguishes destructive operations (danger variant) and can require the
 * user to type a confirmation phrase for irreversible actions.
 *
 * Usage:
 *   const { confirm } = useConfirm()
 *   if (await confirm({ title: '刪除用戶？', message: '此操作無法復原', variant: 'danger' })) {
 *     // proceed
 *   }
 *
 * A single <ConfirmHost /> must be mounted once per app (see ConsoleLayout/App.vue).
 */
export interface ConfirmOptions {
  title: string
  message?: string
  confirmText?: string
  cancelText?: string
  /** 'danger' renders the confirm button red — use for destructive operations. */
  variant?: 'default' | 'danger'
  /**
   * If set, the user must type this exact phrase before the confirm button enables.
   * Reserve for irreversible operations (delete user, batch apply, unbind).
   */
  requireText?: string
}

interface ConfirmState extends Required<Omit<ConfirmOptions, 'requireText'>> {
  open: boolean
  requireText: string | null
  typed: string
}

const DEFAULTS: ConfirmState = {
  open: false,
  title: '',
  message: '',
  confirmText: '確認',
  cancelText: '取消',
  variant: 'default',
  requireText: null,
  typed: '',
}

// Module-level singleton state shared between the caller (useConfirm) and the
// single mounted host (useConfirmHost).
const state = reactive<ConfirmState>({ ...DEFAULTS })
let resolver: ((value: boolean) => void) | null = null

function settle(value: boolean): void {
  state.open = false
  state.typed = ''
  const resolve = resolver
  resolver = null
  resolve?.(value)
}

export function useConfirm() {
  function confirm(options: ConfirmOptions): Promise<boolean> {
    // If a previous dialog is somehow still open, cancel it first (fail-closed).
    if (resolver) settle(false)

    Object.assign(state, {
      ...DEFAULTS,
      ...options,
      requireText: options.requireText ?? null,
      open: true,
      typed: '',
    })

    return new Promise<boolean>((resolve) => {
      resolver = resolve
    })
  }

  return { confirm }
}

/** For the single mounted <ConfirmHost />. Do not use in pages. */
export function useConfirmHost() {
  return {
    state,
    accept: () => settle(true),
    cancel: () => settle(false),
  }
}
